import { useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { CreditCard } from 'lucide-react'
import React, { useState } from 'react'
import { toast } from 'sonner'

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from 'components/ui/sheet'
import { Badge, Button } from 'slab'
import {
  getGetBrokersMembersMemberIdStripeIssuingCardsQueryKey,
  getGetBrokersUnprocessedEnrollmentsIdQueryKey,
  useGetBrokersMembersMemberIdStripeIssuingCards,
  usePostBrokersMembersMemberIdStripeIssuingCards,
  usePostBrokersMembersMemberIdStripeIssuingCardsCardIdRemap,
} from 'utils/web_api'
import {
  type BrokersEnrollmentEnriched,
  type BrokersEnrollmentMappingOption,
  type BrokersPaginatedStripeIssuingCardsList,
  type BrokersStripeIssuingCard,
} from 'utils/web_api.schemas'

interface EnrollmentPaymentMethodRemapSheetProps {
  memberId: string
  selectedEnrollment: BrokersEnrollmentEnriched
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

const getCoverageType = (category: string) => {
  if (
    category.includes('medical') ||
    category.includes('medicare') ||
    category === 'cobra' ||
    category === 'subsidy'
  ) {
    return 'Medical'
  }
  if (category.includes('dental')) {
    return 'Dental'
  }
  if (category.includes('vision') || category === 'vsp') {
    return 'Vision'
  }
  return 'Other'
}

interface CardDisplayProps {
  card: BrokersStripeIssuingCard
  isCurrent: boolean
  isSelected?: boolean
  onClick?: () => void
  selectedEnrollment: BrokersEnrollmentEnriched
}

const CardDisplay: React.FC<CardDisplayProps> = ({
  card,
  isCurrent,
  isSelected = false,
  onClick,
  selectedEnrollment,
}) => {
  const Element = onClick ? 'button' : 'div'

  return (
    <Element
      onClick={onClick}
      className={clsx('w-full text-left rounded-lg p-4 border', {
        // Current card styling
        'border-thatch-purple-active border-2 bg-thatch-purple-background':
          isCurrent,
        // Selectable card styling
        'border-gray-200 bg-white hover:bg-gray-50 shadow transition-colors':
          !isCurrent,
        // Selected state
        'border-thatch-purple-active': isSelected,
      })}
    >
      {/* Card Header */}
      <div
        className={clsx(
          'flex items-center justify-between gap-2 pb-3 mb-3 border-b',
          {
            'border-b-thatch-purple-active': isCurrent,
            'border-b-gray-100': !isCurrent,
          }
        )}
      >
        <div className='flex items-center gap-2'>
          <CreditCard
            className={clsx('h-4 w-4', {
              'text-thatch-purple-active': isCurrent,
              'text-gray-500': !isCurrent,
            })}
          />
          <h3
            className={clsx('text-sm font-medium', {
              'text-thatch-purple-active': isCurrent,
              'text-gray-900': !isCurrent,
            })}
          >
            Card ••••{card.last4}
          </h3>
        </div>
        <Badge type={card.remote_status === 'active' ? 'success' : 'neutral'}>
          {card.remote_status.charAt(0).toUpperCase() +
            card.remote_status.slice(1)}
        </Badge>
      </div>

      {/* Enrollments */}
      <div className='space-y-2'>
        {card.enrollments && card.enrollments.length > 0
          ? (
              card.enrollments.map((enrollment: BrokersEnrollmentMappingOption) => (
                <div
                  key={enrollment.id}
                  className={clsx(
                    'border rounded-lg p-3 border-gray-100 bg-gray-50'
                  )}
                >
                  <div className='flex items-center justify-between'>
                    <div className='flex items-center gap-2'>
                      <Badge
                        type={
                      selectedEnrollment.coverage === enrollment.coverage_type
                        ? 'success'
                        : 'neutral'
                    }
                      >
                        {getCoverageType(enrollment.category)}
                      </Badge>
                      <div
                        className={clsx(
                          'text-sm text-ellipsis overflow-hidden flex-0 text-gray-600'
                        )}
                      >
                        {enrollment.carrier_name} -{' '}
                        {enrollment.plan_name || enrollment.title}
                      </div>
                    </div>
                    <div className='text-sm text-gray-500'>
                      {enrollment.plan_year}
                    </div>
                  </div>
                </div>
              ))
            )
          : (
            <div
              className={clsx('text-sm italic p-3 rounded', {
                'text-thatch-purple-active bg-thatch-purple-background':
                isCurrent,
                'text-gray-500 bg-gray-50': !isCurrent,
              })}
            >
              No enrollments associated with this card
            </div>
            )}
      </div>
    </Element>
  )
}

const EnrollmentPaymentMethodRemapSheet: React.FC<
  EnrollmentPaymentMethodRemapSheetProps
> = ({ memberId, selectedEnrollment, isOpen, onOpenChange }) => {
  const queryClient = useQueryClient()
  const [selectedCard, setSelectedCard] =
    useState<BrokersStripeIssuingCard | null>(null)

  const { data: stripeCardsData } =
    useGetBrokersMembersMemberIdStripeIssuingCards(memberId, undefined, {
      query: {
        enabled: isOpen,
      },
    })

  // Helper function to create common mutation options
  const createMutationOptions = (
    successMessage: string,
    errorMessage: string,
    updateCardsCache: (newCard: BrokersStripeIssuingCard) => void
  ) => ({
    onSuccess: (updatedCard: BrokersStripeIssuingCard) => {
      toast.success(successMessage, {
        position: 'top-center',
      })
      updateCardsCache(updatedCard)

      // Invalidate enrollments query since the enrollment's payment method will be updated
      queryClient.invalidateQueries({
        queryKey: getGetBrokersUnprocessedEnrollmentsIdQueryKey(
          selectedEnrollment.id
        ),
      })

      // Cleanup form state
      setSelectedCard(null)
      onOpenChange(false)
    },
    onError: (error: any) => {
      toast.error(
        `${errorMessage}: ${error?.message || 'Unknown error'}`,
        {
          position: 'top-center',
        }
      )
    },
  })

  const remapCardMutation =
    usePostBrokersMembersMemberIdStripeIssuingCardsCardIdRemap({
      mutation: createMutationOptions(
        'Payment method remapped successfully',
        'Failed to remap payment method',
        (updatedCard) => {
          // Update the cards cache with the remapped card
          const cardsQueryKey =
            getGetBrokersMembersMemberIdStripeIssuingCardsQueryKey(
              memberId,
              undefined
            )
          queryClient.setQueryData<BrokersPaginatedStripeIssuingCardsList>(
            cardsQueryKey,
            (oldData) => {
              if (!oldData) return oldData

              return {
                ...oldData,
                data:
                  oldData.data?.map((card) =>
                    card.id === updatedCard.id ? updatedCard : card
                  ) || [],
              }
            }
          )
        }
      ),
    })

  const createCardMutation = usePostBrokersMembersMemberIdStripeIssuingCards({
    mutation: createMutationOptions(
      'Payment method created successfully',
      'Failed to create payment method',
      (newCard) => {
        // Update the cards cache with the new card
        const cardsQueryKey =
          getGetBrokersMembersMemberIdStripeIssuingCardsQueryKey(memberId)
        queryClient.setQueryData<BrokersPaginatedStripeIssuingCardsList>(
          cardsQueryKey,
          (oldData) => {
            if (!oldData) {
              return {
                data: [newCard],
                pagination: undefined,
              }
            }

            return {
              ...oldData,
              data: [newCard, ...(oldData.data || [])],
            }
          }
        )
      }
    ),
  })

  const renderCurrentCard = () => {
    const currentCard = stripeCardsData?.data?.find(
      (card) => card.id === selectedEnrollment.payment_method_id
    )

    if (!currentCard) {
      return (
        <div className='text-sm text-gray-500 italic p-3 bg-gray-50 rounded'>
          No card currently mapped to this enrollment.
        </div>
      )
    }

    return (
      <>
        <div className='relative'>
          <div className='absolute inset-0 flex items-center'>
            <div className='w-full border-t border-gray-200' />
          </div>
          <div className='relative flex justify-center text-xs uppercase'>
            <span className='bg-white px-2 text-gray-500'>Current Card</span>
          </div>
        </div>
        <CardDisplay
          card={currentCard}
          isCurrent
          selectedEnrollment={selectedEnrollment}
        />
      </>
    )
  }

  const renderAvailableCardsSeparator = () => {
    const availableCards =
      stripeCardsData?.data?.filter(
        (card) => card.id !== selectedEnrollment.payment_method_id
      ) || []

    if (availableCards.length === 0) {
      return null
    }

    return (
      <div className='relative'>
        <div className='absolute inset-0 flex items-center'>
          <div className='w-full border-t border-gray-200' />
        </div>
        <div className='relative flex justify-center text-xs uppercase'>
          <span className='bg-white px-2 text-gray-500'>
            Other Available Cards
          </span>
        </div>
      </div>
    )
  }

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent
        className='h-dvh overflow-y-auto'
        aria-describedby='payment-method-remap-sheet'
      >
        <SheetHeader className='pb-4'>
          <SheetTitle>Remap Payment Method</SheetTitle>
        </SheetHeader>
        <div className='space-y-3'>
          {/* Current Card */}
          {renderCurrentCard()}
          <SheetDescription className='mb-4'>
            Choose a payment method to be mapped to this enrollment.
          </SheetDescription>

          {/* Create New Card Option */}
          <button
            onClick={() => {
              createCardMutation.mutate({
                memberId,
                data: {
                  enrollment_id: selectedEnrollment.id,
                },
              })
            }}
            disabled={
              createCardMutation.isPending || remapCardMutation.isPending
            }
            className='w-full text-left border-2 border-dashed border-gray-300 bg-white hover:bg-gray-50 hover:border-gray-400 rounded-lg p-4 transition-colors'
          >
            <div className='flex items-center justify-center gap-2 text-gray-600'>
              <CreditCard className='h-4 w-4' />
              <span className='text-sm font-medium'>
                {createCardMutation.isPending
                  ? 'Creating new card...'
                  : 'Create new card'}
              </span>
            </div>
          </button>

          {/* Existing Cards (excluding current card) */}
          {renderAvailableCardsSeparator()}

          {stripeCardsData?.data
            ?.filter((card) => card.id !== selectedEnrollment.payment_method_id)
            .map((card) => (
              <CardDisplay
                key={card.id}
                card={card}
                isCurrent={false}
                isSelected={card.id === selectedCard?.id}
                onClick={() => setSelectedCard(card)}
                selectedEnrollment={selectedEnrollment}
              />
            ))}
          {(!stripeCardsData?.data || stripeCardsData.data.length === 0) && (
            <div className='text-center py-4'>
              <p className='text-sm text-gray-500'>
                No existing cards found. Create a new card above.
              </p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className='flex gap-2 pt-4 border-t border-gray-200'>
          <Button
            variant='secondary'
            width='full'
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            variant='primary'
            width='full'
            disabled={
              !selectedCard ||
              remapCardMutation.isPending ||
              createCardMutation.isPending
            }
            onClick={() => {
              if (selectedCard) {
                remapCardMutation.mutate({
                  memberId,
                  cardId: selectedCard.id,
                  data: {
                    enrollment_id: selectedEnrollment.id,
                  },
                })
              }
            }}
          >
            {remapCardMutation.isPending ? 'Remapping...' : 'Apply Selection'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}

export default EnrollmentPaymentMethodRemapSheet
